import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { wsUrl, getToken } from "@/lib/api";

export function useRealtime(enabled) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("connecting");
  const retry = useRef(0);

  useEffect(() => {
    if (!enabled || !getToken()) return undefined;
    let ws;
    let closed = false;
    let pingTimer;
    let reconnectTimer;

    const connect = () => {
      setStatus(retry.current ? "reconnecting" : "connecting");
      ws = new WebSocket(wsUrl());
      ws.onopen = () => {
        retry.current = 0;
        setStatus("connected");
        qc.invalidateQueries({ queryKey: ["board"] });
        pingTimer = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "incident") {
          qc.invalidateQueries({ queryKey: ["board"] });
          if (msg.incident_id) qc.invalidateQueries({ queryKey: ["incident", msg.incident_id] });
          qc.invalidateQueries({ queryKey: ["search"] });
        } else if (msg.type === "units") {
          qc.invalidateQueries({ queryKey: ["board"] });
          if (msg.incident_id) qc.invalidateQueries({ queryKey: ["incident", msg.incident_id] });
          qc.invalidateQueries({ queryKey: ["incident"] });
        } else if (msg.type === "config") {
          qc.invalidateQueries({ queryKey: ["config"] });
          qc.invalidateQueries({ queryKey: ["board"] });
          qc.invalidateQueries({ queryKey: ["settings"] });
          qc.invalidateQueries({ queryKey: ["districts"] });
          qc.invalidateQueries({ queryKey: ["alerts"] });
          qc.invalidateQueries({ queryKey: ["alerts-summary"] });
          qc.invalidateQueries({ queryKey: ["location-detail"] });
        }
      };
      ws.onclose = () => {
        clearInterval(pingTimer);
        if (closed) return;
        setStatus("reconnecting");
        const delay = Math.min(15000, 1000 * 2 ** retry.current++);
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      closed = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, [enabled, qc]);

  return status;
}
